/**
 * PI-based horizontal geometry -> `<CoordGeom>`.
 *
 * `<AlignPIs>` stores only the tangent intersection points plus, at each PI, the
 * curve radius and the entry/exit spiral lengths. The `<Start>`/`<PI>`/`<End>`
 * children of those nested `<Curve>`/`<Spiral>` elements are usually *empty*, so
 * every coordinate has to be computed with the classical spiral-curve-spiral
 * layout equations before anything can be drawn.
 *
 * Notation follows road-design practice: for a deflection `D` at the PI,
 * a spiral of length `Ls` into radius `R` has
 *   `theta_s = Ls / 2R`,  `p = Y - R(1 - cos theta_s)`,  `k = X - R sin theta_s`
 * where `X`, `Y` are the spiral's local end coordinates, and the tangent
 * distance from the PI to the start of the entry spiral is
 *   `Ts = k + (R + p) tan(D/2)`   (plus the asymmetric term when the two
 * spirals differ).
 */

import type { AlignPI, CoordGeom, CgElement, Pt2 } from '../landxml/types';
import { azimuth, normAz } from './arc';
import { integrateSpiral } from './spiral';

export interface AlignPIResult {
  coordGeom: CoordGeom;
  warnings: string[];
  /** Computed length, for cross-checking `Alignment/@length`. */
  length: number;
}

interface SpiralConst {
  X: number;
  Y: number;
  theta: number;
  p: number;
  k: number;
  length: number;
  spiType: string;
}

const ZERO_SPIRAL: SpiralConst = { X: 0, Y: 0, theta: 0, p: 0, k: 0, length: 0, spiType: 'clothoid' };

function spiralConst(length: number | undefined, spiType: string, R: number): SpiralConst {
  if (!length || !(length > 0) || !Number.isFinite(R) || R === 0) return ZERO_SPIRAL;
  const loc = integrateSpiral(length, 0, 1 / Math.abs(R), spiType);
  const n = loc.x.length - 1;
  const X = loc.x[n], Y = loc.y[n], theta = loc.theta[n];
  return {
    X, Y, theta,
    p: Y - Math.abs(R) * (1 - Math.cos(theta)),
    k: X - Math.abs(R) * Math.sin(theta),
    length, spiType,
  };
}

function tHat(az: number): [number, number] { return [Math.sin(az), Math.cos(az)]; }
/** Unit normal to the left of travel, in (east, north). */
function nLeft(az: number): [number, number] { return [-Math.cos(az), Math.sin(az)]; }

function signedDelta(azIn: number, azOut: number): number {
  let d = azOut - azIn;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

export function alignPIsToCoordGeom(pis: AlignPI[], name?: string): AlignPIResult {
  const warnings: string[] = [];
  const warn = (m: string) => { if (warnings.length < 100) warnings.push(m); };
  const elements: CgElement[] = [];
  const label = name ? `Alignment "${name}"` : 'Alignment';

  const pts: Pt2[] = [];
  for (const pi of pis) {
    if (pi.pi.x === undefined || pi.pi.y === undefined) {
      warn(`${label}: a PI has no coordinates and was skipped`);
      continue;
    }
    pts.push({ x: pi.pi.x, y: pi.pi.y, z: pi.pi.z });
  }
  if (pts.length < 2) {
    return { coordGeom: { elements: [] }, warnings, length: 0 };
  }

  let cursor: Pt2 = pts[0];
  let length = 0;

  const line = (a: Pt2, b: Pt2): void => {
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    if (d < 1e-9) return;
    elements.push({ kind: 'Line', start: { ...a }, end: { ...b }, length: d });
    length += d;
  };

  for (let i = 1; i < pts.length - 1; i++) {
    const P = pts[i];
    const src = pis[i];
    const azIn = azimuth(P.x - pts[i - 1].x, P.y - pts[i - 1].y);
    const azOut = azimuth(pts[i + 1].x - P.x, pts[i + 1].y - P.y);
    const delta = signedDelta(azIn, azOut);
    const D = Math.abs(delta);
    const R = src?.curve1?.radius;

    if (!R || !(R > 0) || D < 1e-9) {
      if (R && R > 0 && D < 1e-9) {
        warn(`${label}: PI ${i + 1} has a radius but no deflection; treated as a straight`);
      }
      line(cursor, P);
      cursor = P;
      continue;
    }
    if (src?.curve2) {
      warn(`${label}: PI ${i + 1} is a compound curve (Curve2); approximated with the Curve1 radius`);
    }

    // Azimuth increases clockwise, so a positive deflection turns right.
    const rot: 'cw' | 'ccw' = delta > 0 ? 'cw' : 'ccw';
    const s = rot === 'ccw' ? 1 : -1; // multiplier that points nLeft at the centre

    const s1 = spiralConst(src?.inSpiral?.length, src?.inSpiral?.spiType ?? 'clothoid', R);
    const s2 = spiralConst(src?.outSpiral?.length, src?.outSpiral?.spiType ?? 'clothoid', R);

    const halfTan = Math.tan(D / 2);
    const asym = Math.abs(s2.p - s1.p) < 1e-12 || D < 1e-6 ? 0 : (s2.p - s1.p) / Math.sin(D);
    const Ts1 = s1.k + (R + s1.p) * halfTan + asym;
    const Ts2 = s2.k + (R + s2.p) * halfTan - asym;

    let deltaC = D - s1.theta - s2.theta;
    if (deltaC < -1e-9) {
      warn(`${label}: PI ${i + 1} - the spirals (${s1.theta.toFixed(4)} + ${s2.theta.toFixed(4)} rad) use up more than the ${D.toFixed(4)} rad deflection; the circular arc was dropped`);
      deltaC = 0;
    }

    const tIn = tHat(azIn), tOut = tHat(azOut);
    const TS: Pt2 = { x: P.x - Ts1 * tIn[0], y: P.y - Ts1 * tIn[1] };
    const ST: Pt2 = { x: P.x + Ts2 * tOut[0], y: P.y + Ts2 * tOut[1] };

    // Tangent run into the curve.
    const back = Math.hypot(TS.x - cursor.x, TS.y - cursor.y);
    const alongIn = (TS.x - cursor.x) * tIn[0] + (TS.y - cursor.y) * tIn[1];
    if (back > 1e-6 && alongIn < -1e-6) {
      warn(`${label}: PI ${i + 1} - the curve overlaps the previous element by ${(-alongIn).toFixed(3)}; geometry may be wrong`);
    }
    line(cursor, TS);

    // Entry spiral: TS -> SC.
    let SC: Pt2 = TS;
    let azSC = azIn;
    if (s1.length > 0) {
      const nIn = nLeft(azIn);
      SC = {
        x: TS.x + s1.X * tIn[0] + s * s1.Y * nIn[0],
        y: TS.y + s1.X * tIn[1] + s * s1.Y * nIn[1],
      };
      azSC = normAz(azIn - s * s1.theta);
      elements.push({
        kind: 'Spiral', rot, spiType: s1.spiType, length: s1.length,
        radiusStart: Infinity, radiusEnd: R,
        start: { ...TS }, end: { ...SC }, dirStart: azIn, dirEnd: azSC,
        theta: s1.theta,
      });
      length += s1.length;
    }

    // Circular arc: SC -> CS.
    let CS: Pt2 = SC;
    let azCS = azSC;
    if (deltaC > 1e-12) {
      const nSC = nLeft(azSC);
      const cx = SC.x + s * R * nSC[0];
      const cy = SC.y + s * R * nSC[1];
      const t0 = Math.atan2(SC.y - cy, SC.x - cx);
      const t1 = t0 + s * deltaC;
      CS = { x: cx + R * Math.cos(t1), y: cy + R * Math.sin(t1) };
      azCS = normAz(azSC - s * deltaC);
      elements.push({
        kind: 'Curve', rot, radius: R, delta: deltaC, length: R * deltaC,
        start: { ...SC }, center: { x: cx, y: cy }, end: { ...CS },
        dirStart: azSC, dirEnd: azCS,
      });
      length += R * deltaC;
    }

    // Exit spiral: CS -> ST.
    if (s2.length > 0) {
      elements.push({
        kind: 'Spiral', rot, spiType: s2.spiType, length: s2.length,
        radiusStart: R, radiusEnd: Infinity,
        start: { ...CS }, end: { ...ST }, dirStart: azCS, dirEnd: azOut,
        theta: s2.theta,
      });
      length += s2.length;
      // Cross-check ST. `s2.X/Y` are measured from the spiral's *tangent* end, so
      // predicting forward from CS needs its own integration (curvature R -> INF).
      const fwd = integrateSpiral(s2.length, 1 / R, 0, s2.spiType);
      const m = fwd.x.length - 1;
      const tCS = tHat(azCS), nCS = nLeft(azCS);
      const predX = CS.x + fwd.x[m] * tCS[0] + s * fwd.y[m] * nCS[0];
      const predY = CS.y + fwd.x[m] * tCS[1] + s * fwd.y[m] * nCS[1];
      const err = Math.hypot(predX - ST.x, predY - ST.y);
      if (err > Math.max(1e-3, 1e-5 * s2.length)) {
        warn(`${label}: PI ${i + 1} - exit spiral closes ${err.toFixed(4)} away from the computed ST`);
      }
    } else if (Math.hypot(CS.x - ST.x, CS.y - ST.y) > 1e-6) {
      line(CS, ST);
    }
    cursor = ST;
  }

  line(cursor, pts[pts.length - 1]);
  return { coordGeom: { elements }, warnings, length };
}

/**
 * The geometry a viewer should draw for an alignment: `<CoordGeom>` when present,
 * otherwise the PI list expanded into one.
 */
export function alignmentCoordGeom(a: { name: string; coordGeom: CoordGeom; alignPIs: AlignPI[] }):
{ coordGeom: CoordGeom; warnings: string[]; fromPIs: boolean } {
  if (a.coordGeom.elements.length) return { coordGeom: a.coordGeom, warnings: [], fromPIs: false };
  if (a.alignPIs.length >= 2) {
    const r = alignPIsToCoordGeom(a.alignPIs, a.name);
    return { coordGeom: r.coordGeom, warnings: r.warnings, fromPIs: true };
  }
  return { coordGeom: a.coordGeom, warnings: [], fromPIs: false };
}
